import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Card from '../components/ui/Card';
import NoticiaCardImage from '../components/NoticiaCardImage';
import SEO from '../components/SEO';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Eye,
  Minus,
} from 'lucide-react';

// ─── Category system ──────────────────────────────────────────────

const MAIN_CATEGORIES = ['Economia', 'Política', 'Esportes', 'Tecnologia', 'Beleza', 'Famosos', 'Saúde', 'Educação', 'Ciência', 'Crimes', 'Games'];

const CATEGORY_MAP = {
  Basquete: 'Esportes', Futebol: 'Esportes', Tênis: 'Esportes', Esportes: 'Esportes', Esporte: 'Esportes',
  Entretenimento: 'Famosos', 'Reality Show': 'Famosos', Famosos: 'Famosos',
  Jogos: 'Games', Games: 'Games',
  Tecnologia: 'Tecnologia',
  Loterias: 'Economia', Economia: 'Economia',
  Política: 'Política', Geopolítica: 'Política',
  Beleza: 'Beleza',
  Saúde: 'Saúde',
  Educação: 'Educação',
  Ciência: 'Ciência', Ciencia: 'Ciência',
  Crimes: 'Crimes', Segurança: 'Crimes',
};

function normalizeCategory(cat) {
  if (!cat) return null;
  const trimmed = cat.trim();
  if (CATEGORY_MAP[trimmed]) return CATEGORY_MAP[trimmed];
  // Handle multi-value ("Esportes, Famosos") and case variations ("esportes")
  const parts = trimmed.split(',').map(p => p.trim());
  for (const part of parts) {
    const direct = CATEGORY_MAP[part];
    if (direct) return direct;
    const titleCase = part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    if (CATEGORY_MAP[titleCase]) return CATEGORY_MAP[titleCase];
  }
  return trimmed;
}


const CATEGORY_COLORS = {
  Economia:   { hex: '#2563eb', hexDark: '#60a5fa' },
  Política:   { hex: '#dc2626', hexDark: '#f87171' },
  Esportes:   { hex: '#16a34a', hexDark: '#4ade80' },
  Tecnologia: { hex: '#9333ea', hexDark: '#c084fc' },
  Beleza:     { hex: '#ec4899', hexDark: '#f472b6' },
  Famosos:    { hex: '#f59e0b', hexDark: '#fbbf24' },
  Saúde:      { hex: '#059669', hexDark: '#34d399' },
  Educação:   { hex: '#0891b2', hexDark: '#22d3ee' },
  Ciência:    { hex: '#6366f1', hexDark: '#a5b4fc' },
  Crimes:     { hex: '#78716c', hexDark: '#a8a29e' },
  Games:      { hex: '#7c3aed', hexDark: '#a78bfa' },
};

const DEFAULT_COLOR = { hex: '#6b7280', hexDark: '#9ca3af' };

const CATEGORY_API_FILTERS = {
  Economia:   'Economia,Loterias',
  Política:   'Política,Geopolítica,Politica',
  Esportes:   'Esportes,Futebol,Basquete,Tênis,esportes,futebol',
  Tecnologia: 'Tecnologia',
  Beleza:     'Beleza',
  Famosos:    'Famosos,Entretenimento,Reality Show',
  Saúde:      'Saúde',
  Educação:   'Educação',
  Ciência:    'Ciência,Ciencia',
  Crimes:     'Crimes,Segurança',
  Games:      'Games,Jogos',
};

function useDarkMode() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

function getCategoryColor(categoria, isDark) {
  const norm = normalizeCategory(categoria);
  const colors = CATEGORY_COLORS[norm] || DEFAULT_COLOR;
  return isDark ? colors.hexDark : colors.hex;
}

// ─── Stock ticker config ──────────────────────────────────────────

const TOP_STOCKS = [
  { sigla: 'PETR4', nome: 'Petrobras',  ticker: 'PETR4.SA', cd_cvm: 9512 },
  { sigla: 'VALE3', nome: 'Vale',        ticker: 'VALE3.SA', cd_cvm: 4170 },
  { sigla: 'ITUB4', nome: 'Itaú',        ticker: 'ITUB4.SA', cd_cvm: 19348 },
  { sigla: 'BBDC4', nome: 'Bradesco',    ticker: 'BBDC4.SA', cd_cvm: 906 },
  { sigla: 'B3SA3', nome: 'B3',          ticker: 'B3SA3.SA', cd_cvm: 21610 },
  { sigla: 'WEGE3', nome: 'WEG',         ticker: 'WEGE3.SA', cd_cvm: 5410 },
  { sigla: 'ABEV3', nome: 'Ambev',       ticker: 'ABEV3.SA', cd_cvm: 23264 },
  { sigla: 'SUZB3', nome: 'Suzano',      ticker: 'SUZB3.SA', cd_cvm: 13986 },
  { sigla: 'PRIO3', nome: 'PRIO',        ticker: 'PRIO3.SA', cd_cvm: 22187 },
  { sigla: 'EMBJ3', nome: 'Embraer',     ticker: 'EMBJ3.SA', cd_cvm: 20087 },
];

// ─── Component ────────────────────────────────────────────────────

export default function HomePage() {
  const navigate = useNavigate();
  const isDark = useDarkMode();

  const [activeCategory, setActiveCategory] = useState(null);
  const [heroArticle, setHeroArticle] = useState(null);
  const [subHeadlines, setSubHeadlines] = useState([]);
  const [secondaryRow, setSecondaryRow] = useState([]);
  const [sidebarColumns, setSidebarColumns] = useState({ left: [], right: [] });
  const [sidebarCategories, setSidebarCategories] = useState({ left: '', right: '' });
  const [loadingNews, setLoadingNews] = useState(true);

  const [stockQuotes, setStockQuotes] = useState({});
  const [loadingStocks, setLoadingStocks] = useState(true);

  // Fetch stocks
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const results = await Promise.allSettled(
          TOP_STOCKS.map(s => axios.get(`/stocks/market/${s.ticker}`))
        );
        const quotes = {};
        results.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            quotes[TOP_STOCKS[i].sigla] = result.value.data;
          }
        });
        setStockQuotes(quotes);
      } catch (err) {
        console.error('Erro ao carregar cotações', err);
      } finally {
        setLoadingStocks(false);
      }
    };
    fetchStocks();
  }, []);

  // Fetch news — responds to activeCategory changes
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoadingNews(true);

        const heroParams = { limit: 25, sort: 'views' };
        if (activeCategory) heroParams.categorias = CATEGORY_API_FILTERS[activeCategory];

        const [viewsRes, esportesRes, economiaRes] = await Promise.allSettled([
          axios.get('/news', { params: heroParams }),
          axios.get('/news', { params: { limit: 5, categorias: CATEGORY_API_FILTERS.Esportes } }),
          axios.get('/news', { params: { limit: 5, categorias: CATEGORY_API_FILTERS.Economia } }),
        ]);
        const viewsSorted = (viewsRes.status === 'fulfilled' && viewsRes.value.data.success
          ? viewsRes.value.data.data : []).filter(a => a.imageUrl && a.imageUrl.trim() !== '' && a.imageUrl !== 'null');
        const esportesData = (esportesRes.status === 'fulfilled' && esportesRes.value.data.success
          ? esportesRes.value.data.data : []).filter(a => a.imageUrl && a.imageUrl.trim() !== '' && a.imageUrl !== 'null');
        const economiaData = (economiaRes.status === 'fulfilled' && economiaRes.value.data.success
          ? economiaRes.value.data.data : []).filter(a => a.imageUrl && a.imageUrl.trim() !== '' && a.imageUrl !== 'null');
        const usedSlugs = new Set();

        // Hero
        const hero = viewsSorted[0] || null;
        if (hero) usedSlugs.add(hero.slug);
        setHeroArticle(hero);

        // Sub-headlines (next 3 most viewed)
        const subs = viewsSorted.slice(1, 4).filter(a => !usedSlugs.has(a.slug));
        subs.forEach(a => usedSlugs.add(a.slug));
        setSubHeadlines(subs);

        // Secondary row (next 3)
        const secondary = viewsSorted.slice(4, 7).filter(a => !usedSlugs.has(a.slug));
        secondary.forEach(a => usedSlugs.add(a.slug));
        setSecondaryRow(secondary);

        // Sidebar — dedicated API calls per category
        setSidebarCategories({ left: 'Esportes', right: 'Economia' });
        setSidebarColumns({
          left: esportesData.filter(a => !usedSlugs.has(a.slug)),
          right: economiaData.filter(a => !usedSlugs.has(a.slug)),
        });
      } catch (err) {
        console.error('Erro ao carregar notícias', err);
      } finally {
        setLoadingNews(false);
      }
    };
    fetchNews();
  }, [activeCategory]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return dateStr;
      const [, year, month, day] = match.map(Number);
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    } catch {
      return dateStr;
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────

  const renderSidebarColumn = (articles, categoryName) => {
    const color = getCategoryColor(categoryName, isDark);
    const bgColor = getCategoryColor(categoryName, false);
    return (
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="px-4 py-2.5" style={{ backgroundColor: bgColor }}>
          <h3 className="text-white font-bold text-sm uppercase tracking-wide">{categoryName}</h3>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {articles.map(article => (
            <div
              key={article.slug}
              className="flex gap-3 p-3 cursor-pointer hover:bg-[var(--accent-soft)] transition-colors"
              onClick={() => navigate(`/noticias/${article.slug}`)}
            >
              <div className="w-20 h-14 rounded overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                {article.imageUrl ? (
                  <img
                    src={article.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    Sem img
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4
                  className="text-sm font-semibold line-clamp-2 leading-tight cursor-pointer hover:underline"
                  style={{ color }}
                >
                  {article.title}
                </h4>
                <p className="text-xs text-[var(--muted)] mt-1 line-clamp-1">{article.summary}</p>
              </div>
            </div>
          ))}
          {articles.length === 0 && (
            <div className="p-4 text-center text-sm text-[var(--muted)]">Nenhuma notícia</div>
          )}
        </div>
      </div>
    );
  };

  const newsSkeleton = (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-6">
        <div className="animate-pulse aspect-video rounded-xl bg-gray-200 dark:bg-gray-700" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="animate-pulse h-48 rounded-xl bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      </div>
      <div className="lg:col-span-4 space-y-6">
        <div className="animate-pulse h-64 rounded-xl bg-gray-200 dark:bg-gray-700" />
        <div className="animate-pulse h-64 rounded-xl bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );

  const heroColor = heroArticle ? getCategoryColor(heroArticle.categoria, false) : '#2563eb';

  return (
    <div>
      <SEO
        title="Dados, Política e Notícias do Brasil"
        description="Guia do Povo reúne dados públicos, política, ações da bolsa, notícias e preços de gás de cozinha em um só lugar — com fontes oficiais como ANP, CVM, TSE e IBGE."
        path="/"
      />
      {/* ─── Stock Ticker ──────────────────────────────────── */}
      <div className="mb-4">
        <div className="relative overflow-hidden">
          <style>{`
            @keyframes ticker-scroll {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .ticker-track {
              animation: ticker-scroll 35s linear infinite;
            }
            .ticker-track:hover {
              animation-play-state: paused;
            }
          `}</style>
          <div className="ticker-track flex gap-3" style={{ width: 'max-content' }}>
            {[...TOP_STOCKS, ...TOP_STOCKS].map((stock, idx) => {
              const q = stockQuotes[stock.sigla];
              const loading = loadingStocks && !q;
              const pct = q?.dayChangePct ?? 0;
              const price = q?.currentPrice ?? 0;
              const isPositive = pct > 0;
              const isNegative = pct < 0;

              return (
                <div
                  key={`${stock.sigla}-${idx}`}
                  onClick={() => navigate('/stocks', { state: { cdcvm: stock.cd_cvm } })}
                  className="cursor-pointer flex-shrink-0 w-36 rounded-xl border border-[var(--border)] bg-[var(--card)] p-3 hover:shadow-md hover:-translate-y-0.5 transition-shadow duration-200"
                >
                  {loading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full mt-2" />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-1.5">
                        <div className="font-bold text-sm text-[var(--text)]">{stock.sigla}</div>
                        {q?.logoUrl && (
                          <img
                            src={q.logoUrl}
                            alt={`${stock.sigla} logo`}
                            className="w-5 h-5 rounded-md object-contain bg-white border border-[var(--border)] p-0.5 flex-shrink-0"
                          />
                        )}
                      </div>
                      <div className="text-xs text-[var(--muted)] truncate mb-2">{stock.nome}</div>
                      <div className="text-base font-semibold text-[var(--text)] tabular-nums">
                        {price > 0
                          ? `R$ ${price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : <span className="text-[var(--muted)] text-sm">—</span>
                        }
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-medium mt-0.5 tabular-nums ${
                        isPositive ? 'text-emerald-600 dark:text-emerald-400'
                        : isNegative ? 'text-red-500 dark:text-red-400'
                        : 'text-[var(--muted)]'
                      }`}>
                        {isPositive ? <TrendingUp size={12} />
                          : isNegative ? <TrendingDown size={12} />
                          : <Minus size={12} />}
                        {pct !== 0
                          ? `${isPositive ? '+' : ''}${pct.toFixed(2)}%`
                          : price > 0 ? '0,00%' : '—'
                        }
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Category Nav Bar ──────────────────────────────── */}
      <div className="flex gap-1 overflow-x-auto pb-1 mb-6 border-b border-[var(--border)]">
        {MAIN_CATEGORIES.map(cat => {
          const isActive = activeCategory === cat;
          const color = getCategoryColor(cat, isDark);
          const bgColor = getCategoryColor(cat, false);
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(isActive ? null : cat)}
              className={`px-4 py-2 text-sm font-bold whitespace-nowrap rounded-t-lg transition-all ${
                isActive ? 'text-white' : 'hover:opacity-80'
              }`}
              style={isActive
                ? { backgroundColor: bgColor, color: '#fff', borderBottom: `3px solid ${bgColor}` }
                : { color, borderBottom: `3px solid transparent` }
              }
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* ─── Main Grid ─────────────────────────────────────── */}
      {loadingNews ? newsSkeleton : <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left: Hero + Secondary */}
        <div className="lg:col-span-8 space-y-6">

          {/* Hero Section */}
          {heroArticle && (
            <div>
              <div
                className="relative group cursor-pointer rounded-t-xl overflow-hidden"
                onClick={() => navigate(`/noticias/${heroArticle.slug}`)}
              >
                <div className="aspect-video bg-gray-200 dark:bg-gray-800">
                  {heroArticle.imageUrl ? (
                    <img
                      src={heroArticle.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full" style={{ backgroundColor: heroColor }} />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span
                    className="inline-block px-2.5 py-1 rounded text-xs font-bold text-white mb-3"
                    style={{ backgroundColor: heroColor }}
                  >
                    {normalizeCategory(heroArticle.categoria)}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight line-clamp-3 drop-shadow-lg">
                    {heroArticle.title}
                  </h2>
                  <div className="flex items-center gap-3 mt-2 text-white/70 text-sm">
                    <span>{formatDate(heroArticle.dataGeracao)}</span>
                    <span className="flex items-center gap-1">
                      <Eye size={14} />
                      {(heroArticle.views || 0).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sub-headlines bar */}
              {subHeadlines.length > 0 && (
                <div
                  className="rounded-b-xl p-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
                  style={{ backgroundColor: heroColor }}
                >
                  {subHeadlines.map(article => (
                    <div
                      key={article.slug}
                      className="cursor-pointer hover:bg-white/10 rounded p-2 transition-colors"
                      onClick={() => navigate(`/noticias/${article.slug}`)}
                    >
                      <p className="text-white text-sm font-medium line-clamp-2 leading-snug">
                        {article.title}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Secondary Row */}
          {secondaryRow.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {secondaryRow.map(article => {
                const color = getCategoryColor(article.categoria, isDark);
                return (
                  <div
                    key={article.slug}
                    className="bg-[var(--card)] border border-[var(--border)] rounded-xl overflow-hidden cursor-pointer group hover:shadow-lg transition-all"
                    onClick={() => navigate(`/noticias/${article.slug}`)}
                  >
                    <NoticiaCardImage
                      imageUrl={article.imageUrl}
                      title={article.title}
                      viewMode="grid"
                      slug={article.slug}
                    />
                    <div className="p-4">
                      <span className="text-xs font-bold uppercase" style={{ color }}>
                        {normalizeCategory(article.categoria)}
                      </span>
                      <h3 className="font-bold text-[var(--text)] mt-1 line-clamp-2 group-hover:underline leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-[var(--muted)] mt-1 line-clamp-2">{article.summary}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          {renderSidebarColumn(sidebarColumns.left, sidebarCategories.left)}
          {renderSidebarColumn(sidebarColumns.right, sidebarCategories.right)}
        </aside>
      </div>}

      {/* ─── Feature Card ──────────────────────────────────── */}
      <div className="mt-8 mb-8">
        <Card
          onClick={() => navigate('/dados')}
          className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800"
        >
          <div className="flex items-start gap-6">
            <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex-shrink-0 group-hover:scale-110 transition-transform">
              <BarChart3 size={32} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-2xl font-bold text-[var(--text)] group-hover:text-blue-600 transition-colors">
                  Balança Comercial Brasileira
                </h3>
                <ArrowRight
                  size={24}
                  className="text-[var(--muted)] group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0"
                />
              </div>
              <p className="text-base text-[var(--muted)] mb-4 leading-relaxed">
                Análise detalhada do comércio exterior brasileiro por estado, com dados históricos
                de 2000 a 2025 e visualizações interativas. Explore exportações, importações e
                saldo comercial por estado e município.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-blue-500" />
                  <span className="text-sm text-[var(--muted)]">Dados de 2000-2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye size={16} className="text-blue-500" />
                  <span className="text-sm text-[var(--muted)]">Página mais acessada</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
